/**
 * PageListLayout - Reusable layout template for all list/registry pages.
 * Provides consistent structure: breadcrumbs, header, filters, content grid/table, pagination.
 * Used by ~15 list pages across the application.
 */

import type { ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Pagination,
  Alert,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface CreateButtonConfig {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
}

interface PaginationConfig {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

interface EmptyStateConfig {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface PageListLayoutProps {
  /** Page title */
  title: string;
  /** Optional page subtitle */
  subtitle?: string;
  /** Breadcrumb navigation items */
  breadcrumbs?: BreadcrumbItem[];
  /** Create/Add button configuration */
  createButton?: CreateButtonConfig;
  /** Filter bar content (rendered inside a glass card) */
  filters?: ReactNode;
  /** View mode toggle: 'grid' | 'table' | 'both' */
  viewMode?: 'grid' | 'table' | 'both';
  /** Current active view when viewMode is 'both' */
  activeView?: 'grid' | 'table';
  /** Callback when view mode changes */
  onViewChange?: (view: 'grid' | 'table') => void;
  /** Main content area */
  children: ReactNode;
  /** Pagination configuration */
  pagination?: PaginationConfig;
  /** Loading state - shows skeleton */
  loading?: boolean;
  /** Error message */
  error?: string;
  /** Empty state configuration */
  emptyState?: EmptyStateConfig;
  /** Whether to show the empty state */
  isEmpty?: boolean;
  /** Error dismiss handler */
  onErrorDismiss?: () => void;
  /** Extra actions to render in the header area */
  headerActions?: ReactNode;
}

/**
 * Reusable list page layout with modern glassmorphism design.
 */
export const PageListLayout = ({
  title,
  subtitle,
  breadcrumbs,
  createButton,
  filters,
  viewMode,
  activeView = 'table',
  onViewChange,
  children,
  pagination: paginationConfig,
  loading = false,
  error,
  emptyState,
  isEmpty = false,
  onErrorDismiss,
  headerActions,
}: PageListLayoutProps) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 6, animation: 'fade-in 300ms ease-out' }}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            if (isLast || !crumb.href) {
              return (
                <Typography key={crumb.label} variant="body2" color="text.primary" fontWeight={500}>
                  {crumb.label}
                </Typography>
              );
            }
            return (
              <Link
                key={crumb.label}
                component="button"
                variant="body2"
                onClick={() => crumb.href && navigate(crumb.href)}
                sx={{
                  color: 'var(--mui-palette-text-secondary)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { color: 'var(--mui-palette-primary-main)' },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 6 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: subtitle ? 1 : 0 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {headerActions}
          {viewMode === 'both' && onViewChange && (
            <ToggleButtonGroup
              value={activeView}
              exclusive
              onChange={(_, val) => val && onViewChange(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                  px: 2,
                  py: 1,
                  '&.Mui-selected': {
                    background: 'var(--gradient-primary)',
                    color: '#fff',
                    borderColor: 'transparent',
                    '&:hover': {
                      background: 'var(--gradient-primary)',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="table"><TableRowsIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          )}
          {createButton && (
            <Button
              variant="contained"
              color="primary"
              startIcon={createButton.icon}
              onClick={createButton.onClick}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {createButton.label}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={onErrorDismiss}
          sx={{ mb: 4 }}
        >
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      {filters && (
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            background: 'var(--glass-bg-subtle)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {filters}
        </Box>
      )}

      {/* Content Area */}
      {loading ? (
        <Box>
          {/* Skeleton loading */}
          {activeView === 'grid' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 4 }}>
              {[...Array(8)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={180}
                  sx={{
                    borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
                    bgcolor: 'var(--glass-bg-subtle)',
                  }}
                />
              ))}
            </Box>
          ) : (
            <Box>
              <Skeleton variant="rounded" height={48} sx={{ mb: 1, borderRadius: 'var(--mui-shape-customBorderRadius-sm)', bgcolor: 'var(--glass-bg-subtle)' }} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 0.5, borderRadius: 'var(--mui-shape-customBorderRadius-sm)', bgcolor: 'var(--glass-bg-subtle)' }} />
              ))}
            </Box>
          )}
        </Box>
      ) : isEmpty && emptyState ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 16,
            px: 4,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            background: 'var(--glass-bg-subtle)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
              background: 'var(--gradient-primary-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <Typography variant="h4" sx={{ opacity: 0.5 }}>
              +
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {emptyState.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 400 }}>
            {emptyState.description}
          </Typography>
          {emptyState.action && (
            <Button
              variant="contained"
              color="primary"
              onClick={emptyState.action.onClick}
            >
              {emptyState.action.label}
            </Button>
          )}
        </Box>
      ) : (
        children
      )}

      {/* Pagination */}
      {paginationConfig && paginationConfig.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Pagination
            count={paginationConfig.totalPages}
            page={paginationConfig.page}
            onChange={(_, page) => paginationConfig.onChange(page)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default PageListLayout;
